import type {
  ApiCustomizationOption,
  ApiProduct,
  ApiProductDetail,
  ApiSearchProduct,
  ApiTaxon,
} from '../api/types'
import {
  OCCASION_TONES,
  PRODUCT_CARD_TONES,
  PRODUCT_UI,
} from '../constants/api'
import { CATEGORY_FALLBACK_ICONS, ICON_FALLBACK, type IconName } from '../constants/icons'
import { PRODUCT_CONTENT_KEYS } from '../constants/product'
import { taxonPageRoute } from './catalog'
import { discountPercent } from './format'
import { resolveIconName } from './icons'
import {
  looksLikeHtml,
  parseCareInstructionsHtml,
  type CareInstruction,
} from './productContent'

export type { CareInstruction }

export type CatalogCard = {
  id: string
  name: string
  vendor: string
  price: number
  oldPrice: number | null
  discountPercent: number
  badge: string
  tone: (typeof PRODUCT_CARD_TONES)[number]
  to: string
  imageUrl: string | null
}

export type NavCategory = {
  id: string
  label: string
  to: string
  icon?: IconName
  emphasize?: boolean
  children?: NavCategory[]
}

export type OccasionCard = {
  id: string
  label: string
  slug: string
  icon: IconName
  tone: (typeof OCCASION_TONES)[number]
  imageUrl: string | null
}

export type CategoryTile = {
  id: string
  label: string
  to: string
  imageUrl: string | null
  icon: IconName
  tone: (typeof OCCASION_TONES)[number]
  emphasize?: boolean
}

export type ProductOption = {
  id: string
  label: string
  price: number
  compareAtPrice?: number | null
  description?: string
  icon?: IconName
  imageUrl?: string | null
  isDefault?: boolean
  imageIds?: string[]
  sku?: string | null
  /** Resolved PDP content for stem variants (falls back to product defaults). */
  content?: ProductContentView
}

export type ProductContentView = {
  shortDescription: string | null
  description: string | null
  contains: string[]
  careInstructions: CareInstruction[]
}

export type ProductImageView = {
  id: string
  url: string | null
  label: string
  tone: (typeof PRODUCT_CARD_TONES)[number]
}

export type ProductSeller = {
  name: string
  initial: string
  rating: number | null
  reviewCount: number
  location: string | null
  verified: boolean
}

export type ProductViewModel = {
  id: string
  slug: string
  name: string
  primaryTaxonId: string | null
  taxonIds: string[]
  categoryPath: string
  inStock: boolean
  isExpressEligible: boolean
  rating: number | null
  reviewCount: number
  tags: string[]
  badges: Array<{ label: string; tone: 'amber' | 'green'; icon?: IconName }>
  priceNote: string
  prepayNote: string
  /** Full catalog of product + variant images (lookup). */
  images: ProductImageView[]
  /** Product-level image ids shown for every variant. */
  sharedImageIds: string[]
  seller: ProductSeller | null
  /** Product-level defaults; prefer selected stem's `content` on the PDP. */
  content: ProductContentView
  stemOptions: ProductOption[]
  wrapperOptions: ProductOption[]
  addonOptions: ProductOption[]
  breadcrumbs: Array<{ label: string; to: string }>
  cta: typeof PRODUCT_UI.cta
}


export function primaryImageUrl(
  images: Array<{ url: string; isPrimary?: boolean }> | undefined,
): string | null {
  if (!images?.length) return null
  return images.find((image) => image.isPrimary)?.url ?? images[0]?.url ?? null
}

function expressBadgeLabel(product: {
  isExpressEligible: boolean
  expressAvailable?: boolean
}): string {
  const showExpress = product.expressAvailable ?? product.isExpressEligible
  return showExpress ? PRODUCT_UI.expressBadgeShort : ''
}

export function mapProductCard(product: ApiProduct, index: number): CatalogCard {
  const primaryImage = primaryImageUrl(product.images)
  const defaultVariant =
    product.variants.find((variant) => variant.isDefault && variant.isActive) ??
    product.variants.find((variant) => variant.isActive) ??
    product.variants[0]

  const price = defaultVariant?.price ?? product.displayPrice
  const oldPrice = defaultVariant?.compareAtPrice ?? product.compareAtPrice
  const savings =
    oldPrice != null ? discountPercent(oldPrice, price) : 0

  return {
    id: product.id,
    name: product.name,
    vendor: product.primaryTaxon?.name ?? '',
    price,
    oldPrice: savings > 0 ? oldPrice : null,
    discountPercent: savings,
    badge: expressBadgeLabel(product),
    tone: PRODUCT_CARD_TONES[index % PRODUCT_CARD_TONES.length],
    to: `/product/${product.slug}`,
    imageUrl: primaryImage,
  }
}

export function mapSearchProductCard(
  product: ApiSearchProduct,
  index: number,
): CatalogCard {
  const images = Array.isArray(product.images) ? product.images : []
  const primaryImage =
    images.find((image) => image.isPrimary)?.url ?? images[0]?.url ?? null
  const price = product.displayPrice
  const oldPrice = product.compareAtPrice ?? null
  const savings = oldPrice != null ? discountPercent(oldPrice, price) : 0

  return {
    id: product.id,
    name: product.name,
    vendor: '',
    price,
    oldPrice: savings > 0 ? oldPrice : null,
    discountPercent: savings,
    badge: expressBadgeLabel(product),
    tone: PRODUCT_CARD_TONES[index % PRODUCT_CARD_TONES.length],
    to: `/product/${product.slug}`,
    imageUrl: primaryImage,
  }
}

export function mapOccasionCard(taxon: ApiTaxon, index: number): OccasionCard {
  return {
    id: taxon.id,
    label: taxon.name,
    slug: taxon.slug,
    icon: CATEGORY_FALLBACK_ICONS[index % CATEGORY_FALLBACK_ICONS.length],
    tone: OCCASION_TONES[index % OCCASION_TONES.length],
    imageUrl: taxon.imageUrl,
  }
}

export function mapNavCategories(taxons: ApiTaxon[]): NavCategory[] {
  const byId = new Map(taxons.map((taxon) => [taxon.id, taxon]))
  const sorted = taxons
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))

  const roots = sorted.filter((taxon) => !taxon.parentId)
  const childrenByParent = new Map<string, ApiTaxon[]>()
  for (const taxon of sorted) {
    if (!taxon.parentId) continue
    const list = childrenByParent.get(taxon.parentId) ?? []
    list.push(taxon)
    childrenByParent.set(taxon.parentId, list)
  }

  return roots.map((root) => {
    const children = (childrenByParent.get(root.id) ?? []).map((child) => ({
      id: child.id,
      label: child.name,
      to: taxonPath(child, byId),
    }))
    return {
      id: root.id,
      label: root.name,
      to: taxonPath(root, byId),
      children: children.length > 0 ? children : undefined,
    }
  })
}

export function mapCategoryTiles(taxons: ApiTaxon[]): CategoryTile[] {
  const byId = new Map(taxons.map((taxon) => [taxon.id, taxon]))
  const roots = taxons
    .filter((taxon) => !taxon.parentId)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))

  return roots.map((taxon, index) => ({
    id: taxon.id,
    label: taxon.name,
    to: taxonPath(taxon, byId),
    imageUrl: taxon.imageUrl,
    icon: CATEGORY_FALLBACK_ICONS[index % CATEGORY_FALLBACK_ICONS.length],
    tone: OCCASION_TONES[index % OCCASION_TONES.length],
  }))
}

function taxonPath(taxon: ApiTaxon, byId: Map<string, ApiTaxon>): string {
  const pageRoute = taxonPageRoute(taxon.slug)
  if (pageRoute) return pageRoute

  const parent = taxon.parentId ? byId.get(taxon.parentId) : undefined
  const underOccasion =
    taxon.type === 'occasion' ||
    parent?.slug === 'occasion' ||
    parent?.slug === 'occasions' ||
    parent?.type === 'occasion'

  if (underOccasion) {
    if (taxon.slug === 'occasion' || taxon.slug === 'occasions') {
      return '/shop/occasions'
    }
    return `/shop/occasions/${taxon.slug}`
  }

  return `/shop/${taxon.slug}`
}

export function mapProductDetail(product: ApiProductDetail): ProductViewModel {
  const productImages = mapImageViews(product.images, product.id, product.name)
  const productContent = mapContentView(product.content ?? {})

  const variantEntries = (product.variants ?? [])
    .filter((variant) => variant.isActive)
    .map((variant) => {
      const variantImages = mapImageViews(
        variant.images ?? [],
        variant.id,
        `${product.name} · ${variant.label}`,
      )
      return {
        option: {
          id: variant.id,
          label: variant.label,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          isDefault: variant.isDefault,
          imageIds: variantImages.map((image) => image.id),
          sku: variant.sku,
          content: mapVariantContentView(product, variant),
        } satisfies ProductOption,
        images: variantImages,
      }
    })

  const stemOptions: ProductOption[] =
    variantEntries.length > 0
      ? variantEntries.map((entry) => entry.option)
      : [
          {
            id: product.id,
            label: product.name,
            price: product.displayPrice,
            compareAtPrice: product.compareAtPrice,
            imageIds: productImages.map((image) => image.id),
            sku: null,
            content: productContent,
          },
        ]

  const wrapperOptions = mapCustomizationOptions(product.customizations, 'wrapper')
  const addonOptions = (product.addons ?? []).map((addon) => ({
    id: addon.id,
    label: addon.name,
    price: addon.displayPrice,
    icon: ICON_FALLBACK.addon,
    imageUrl: primaryImageUrl(addon.images),
    content: productContent,
  }))

  const images = uniqueImages([
    ...productImages,
    ...variantEntries.flatMap((entry) => entry.images),
  ])
  const sharedImageIds = productImages.map((image) => image.id)

  const categoryName = product.primaryTaxon?.name ?? product.taxons[0]?.name ?? null

  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    primaryTaxonId: product.primaryTaxonId,
    taxonIds: (product.taxons ?? []).map((taxon) => taxon.id),
    categoryPath: product.primaryTaxon
      ? `${capitalize(product.primaryTaxon.type)} / ${product.primaryTaxon.name}`
      : (categoryName ?? ''),
    inStock: product.status === 'active',
    isExpressEligible: product.isExpressEligible,
    rating: null,
    reviewCount: 0,
    tags: product.isExpressEligible ? ['Express'] : [],
    badges: product.isExpressEligible
      ? [{ label: '60-Min', tone: 'green' as const, icon: ICON_FALLBACK.express }]
      : [],
    priceNote: PRODUCT_UI.priceNote,
    prepayNote: PRODUCT_UI.prepayNote,
    images,
    sharedImageIds,
    seller: null,
    content: productContent,
    stemOptions,
    wrapperOptions,
    addonOptions,
    breadcrumbs: [
      { label: 'Back', to: '/' },
      ...(categoryName
        ? [{ label: categoryName, to: `/shop/${product.primaryTaxon?.slug ?? 'all'}` }]
        : []),
      { label: product.name, to: `/product/${product.slug}` },
    ],
    cta: PRODUCT_UI.cta,
  }
}

function mapVariantContentView(
  product: ApiProductDetail,
  variant: ApiProductDetail['variants'][number],
): ProductContentView {
  if (variant.resolvedContent && Object.keys(variant.resolvedContent).length > 0) {
    return mapContentView(variant.resolvedContent)
  }
  const base = product.content ?? {}
  const override = variant.content
  const merged =
    override && Object.keys(override).length > 0 ? { ...base, ...override } : base
  return mapContentView(merged)
}

function mapContentView(content: Record<string, unknown>): ProductContentView {
  return {
    shortDescription: readString(content, PRODUCT_CONTENT_KEYS.shortDescription),
    description: readString(content, PRODUCT_CONTENT_KEYS.description),
    contains: readStringList(content, PRODUCT_CONTENT_KEYS.contains),
    careInstructions: readCareInstructions(content),
  }
}

function readString(
  content: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = content[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function mapImageViews(
  images: Array<{ url: string; alt?: string; sortOrder?: number; isPrimary?: boolean }>,
  ownerId: string,
  fallbackLabel: string,
): ProductImageView[] {
  return images
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((image, index) => ({
      id: `${ownerId}-img-${index}`,
      url: image.url,
      label: image.alt ?? `${fallbackLabel} ${index + 1}`,
      tone: PRODUCT_CARD_TONES[index % PRODUCT_CARD_TONES.length],
    }))
}

function uniqueImages(images: ProductImageView[]): ProductImageView[] {
  const seen = new Set<string>()
  const result: ProductImageView[] = []
  for (const image of images) {
    const key = image.url ?? image.id
    if (seen.has(key)) continue
    seen.add(key)
    result.push(image)
  }
  return result
}

function mapCustomizationOptions(
  customizations: unknown[],
  type: string,
): ProductOption[] {
  return customizations
    .map((item) => (isRecord(item) ? (item as ApiCustomizationOption) : null))
    .filter((item): item is ApiCustomizationOption => Boolean(item))
    .filter((item) => !item.type || item.type === type || item.type === `${type}s`)
    .map((item, index) => ({
      id: item.id ?? `${type}-${index}`,
      label: item.label ?? item.name ?? `Option ${index + 1}`,
      description: item.description,
      price: Number(item.price ?? 0),
      icon: resolveIconName(item.icon, ICON_FALLBACK.wrapper),
    }))
}

function readStringList(
  content: Record<string, unknown>,
  keys: readonly string[],
): string[] {
  for (const key of keys) {
    const value = content[key]
    if (Array.isArray(value)) {
      const items = value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
      if (items.length > 0) return items
    }
    if (typeof value === 'string' && value.trim()) {
      return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
    }
  }
  return []
}

function readCareInstructions(content: Record<string, unknown>): CareInstruction[] {
  for (const key of PRODUCT_CONTENT_KEYS.care) {
    const value = content[key]
    if (Array.isArray(value)) {
      const items = value
        .map((item) => normalizeCareItem(item))
        .filter((item): item is CareInstruction => item != null)
      if (items.length > 0) return items
    }
    if (typeof value === 'string' && value.trim()) {
      if (looksLikeHtml(value)) {
        const fromHtml = parseCareInstructionsHtml(value)
        if (fromHtml.length > 0) return fromHtml
      }
      return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((text) => ({ title: null, text }))
    }
  }
  return []
}

function normalizeCareItem(value: unknown): CareInstruction | null {
  if (typeof value === 'string' && value.trim()) {
    return { title: null, text: value.trim() }
  }
  if (!isRecord(value)) return null
  const titleRaw = value.title ?? value.label
  const textRaw = value.text ?? value.description ?? value.body
  const title = typeof titleRaw === 'string' && titleRaw.trim() ? titleRaw.trim() : null
  const text = typeof textRaw === 'string' ? textRaw.trim() : ''
  if (!text && !title) return null
  return { title, text: text || title || '' }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
